import type {
  DeskFeature,
  DeskShapeFeature,
  EditableFloorPlan,
  IconLabelFeature,
  PolygonFeature,
  TextLabelFeature,
} from '../models/editor-feature';

/**
 * Turns the editor's model back into the layers the save endpoint accepts.
 *
 * Two shapes of submission, chosen by the caller rather than inferred:
 *
 * - **`deskShapes`** (the default) sends the three layers the editor has always sent, with
 *   the floor layer carrying desk shapes only. The backend merges them into the stored layer
 *   and writes its walls and structural fragments straight back out, so nothing the editor
 *   did not show can be lost.
 * - **`full`** additionally sends the border and room layers and every drawn polygon on the
 *   floor. A shape the editor holds and does not send has been deleted.
 *
 * The narrow one stays the default because getting this wrong in that direction is
 * unrecoverable: a client that sends `full` while showing only desk shapes deletes every
 * wall on the plan, and the admin's undo stack does not reach the database.
 */

export type FloorPlanSubmissionScope = 'deskShapes' | 'full';

export interface FloorPlanLayersPayload {
  readonly workplacesLayer: unknown;
  readonly labelLayer: unknown;
  readonly floorLayer: unknown;
  /** Only sent under the `full` scope; the endpoint refuses to interpret them otherwise. */
  readonly borderLayer?: unknown;
  readonly roomLayer?: unknown;
  /** Names which of the two readings above the server should apply. */
  readonly floorLayerScope?: FloorPlanSubmissionScope;
  /**
   * The measured scale, sent only once there is one.
   *
   * Omitted rather than sent as null when the plan has never been measured: the server reads
   * an absent value as "not changing this", so sending null from every save would be the one
   * way a client that cannot measure could erase a scale somebody else established.
   */
  readonly unitsPerMetre?: number;
  /**
   * The plan's timestamp as it was loaded, so the server can refuse a save built on a copy
   * someone else has since replaced. Omitted when the plan was loaded without one, in which
   * case the server does not check.
   */
  readonly lastUpdatedAt?: string;
}

const collection = (
  envelope: Readonly<Record<string, unknown>>,
  features: unknown[],
  fallbackName: string,
): Record<string, unknown> => ({
  // The envelope carries whatever the stored layer had around its features. Its `name` is
  // meaningless as an identifier — real ones include "border bg alkmaar" and "Workplaces 4"
  // — but dropping it on save would still be a change nobody asked for.
  type: 'FeatureCollection',
  name: fallbackName,
  ...envelope,
  features,
});

const pointFeature = (
  coordinates: readonly [number, number],
  properties: Record<string, unknown>,
): Record<string, unknown> => ({
  type: 'Feature',
  geometry: { type: 'Point', coordinates: [coordinates[0], coordinates[1]] },
  properties,
});

const serializeDesk = (desk: DeskFeature): Record<string, unknown> => pointFeature(desk.coordinates, {
  ...desk.unknownProperties,
  id: desk.storedId,
  featureId: desk.featureId,
  rotation: desk.rotation,
  deskType: desk.deskType,
  podId: desk.podId,
  capacity: desk.capacity,
  // Written only when there is one, so the thousands of desks nobody has given an icon do not
  // each gain a null key in every saved plan.
  ...(desk.iconName ? { iconName: desk.iconName } : {}),
  // Same reasoning: only a desk somebody actually resized carries a size.
  ...(desk.iconScale !== undefined && desk.iconScale !== 1 ? { iconScale: desk.iconScale } : {}),
  // What makes a new desk bookable: the server turns this into the Workplaces row the
  // desk's featureId names, and writes back whichever room that row ends up in.
  roomId: desk.roomId,
});

const serializeLabel = (label: TextLabelFeature | IconLabelFeature): Record<string, unknown> => {
  const shared = {
    ...label.unknownProperties,
    id: label.storedId,
    featureId: label.featureId,
    rotation: label.rotation,
    color: label.color,
  };

  if (label.kind === 'iconLabel') {
    return pointFeature(label.coordinates, {
      ...shared,
      kind: 'icon',
      // An icon annotation has no text, but both keys are still written: the mobile app
      // reads annotation content from `label`, and leaving a stale value there would show
      // the text of whatever this feature used to be.
      text: '',
      label: '',
      iconName: label.iconName,
      iconSize: label.iconSize,
    });
  }

  return pointFeature(label.coordinates, {
    ...shared,
    kind: 'text',
    // Written to both keys on purpose: the editor authors `text`, the app reads `label`.
    text: label.text,
    label: label.text,
    fontSize: label.fontSize,
  });
};

const serializeDeskShape = (shape: DeskShapeFeature): Record<string, unknown> => ({
  type: 'Feature',
  geometry: {
    // Always a MultiPolygon, including for a shape the editor has just created. Every desk
    // shape in the stored data is one, and coordinate maths written for one nesting depth
    // reads the other as NaN without throwing anything.
    type: 'MultiPolygon',
    coordinates: shape.coordinates.map((polygon) => polygon.map((ring) => ring.map((vertex) => [vertex[0], vertex[1]]))),
  },
  properties: {
    ...shape.unknownProperties,
    id: shape.storedId,
    featureId: shape.featureId,
    elementType: 'desk',
    rotation: shape.rotation,
    // Written only when there is one. A client that does not know about icons draws the
    // outline, which is still the truth of where the furniture is.
    ...(shape.iconName ? { iconName: shape.iconName } : {}),
  },
});

/**
 * A drawn area, written back at the nesting it was read at.
 *
 * The nesting matters: 23 stored room features are plain `Polygon`s, and promoting them to
 * `MultiPolygon` on the way out would rewrite bytes on every save of a plan nobody edited
 * the rooms of.
 */
const serializePolygon = (polygon: PolygonFeature): Record<string, unknown> => {
  const properties: Record<string, unknown> = {
    ...polygon.unknownProperties,
    id: polygon.storedId,
    featureId: polygon.featureId,
    rotation: polygon.rotation,
  };

  // Written only when it is something. An absent elementType is what every polygon imported
  // before this editor existed carries, and what a plain drawn area carries still — writing
  // an explicit null instead would add a key to thousands of features that never had one.
  if (polygon.elementType) {
    properties.elementType = polygon.elementType;
  }

  // Kept so the thickness of a wall stays editable after it is drawn. Without it, changing a
  // wall from 200mm to 300mm means deleting it and drawing it again.
  if (polygon.centreline && polygon.thickness) {
    properties.centreline = [
      [polygon.centreline[0][0], polygon.centreline[0][1]],
      [polygon.centreline[1][0], polygon.centreline[1][1]],
    ];
    properties.thickness = polygon.thickness;
  }

  const rings = polygon.coordinates.map(
    (part) => part.map((ring) => ring.map((vertex) => [vertex[0], vertex[1]])),
  );

  return {
    type: 'Feature',
    geometry: {
      type: polygon.geometryType,
      coordinates: polygon.geometryType === 'Polygon' ? rings[0] ?? [] : rings,
    },
    properties,
  };
};

export const serializeFloorPlan = (
  floorPlan: EditableFloorPlan,
  scope: FloorPlanSubmissionScope = 'deskShapes',
): FloorPlanLayersPayload => {
  const payload: FloorPlanLayersPayload = {
    workplacesLayer: collection(
      floorPlan.layerEnvelopes.workplaces,
      floorPlan.desks.map(serializeDesk),
      'WorkplacesLayer',
    ),
    labelLayer: collection(
      floorPlan.layerEnvelopes.label,
      floorPlan.labels.map(serializeLabel),
      'LabelLayer',
    ),
    floorLayer: collection(
      floorPlan.layerEnvelopes.floor,
      scope === 'full'
        // Desk shapes last, matching what the backend's own merge produces, so a save that
        // goes out under one scope and comes back under the other does not reorder the layer.
        ? [...floorPlan.floorBackdrop.map(serializePolygon), ...floorPlan.deskShapes.map(serializeDeskShape)]
        : floorPlan.deskShapes.map(serializeDeskShape),
      'FloorLayer',
    ),
    lastUpdatedAt: floorPlan.lastUpdatedAt,
    ...(floorPlan.unitsPerMetre ? { unitsPerMetre: floorPlan.unitsPerMetre } : {}),
  };

  if (scope !== 'full') {
    return payload;
  }

  return {
    ...payload,
    floorLayerScope: 'full',
    borderLayer: collection(floorPlan.layerEnvelopes.border, floorPlan.border.map(serializePolygon), 'BorderLayer'),
    roomLayer: collection(floorPlan.layerEnvelopes.room, floorPlan.rooms.map(serializePolygon), 'RoomLayer'),
  };
};

export default serializeFloorPlan;
